import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Normalise les rôles FR → EN pour la lookup des permissions
const normalizeRole = (role) => {
  const map = { partenaire: 'partner', vendeur: 'vendor' };
  return map[(role || '').toLowerCase()] || (role || 'vendor');
};

// Default permissions — mirrors the hardcoded behaviour before this feature
// NOTE: La règle #1 (scope partenaire octopus/ilek) prévaut TOUJOURS sur le RBAC.
// Un user sans accès au partenaire ILEK ne verra jamais les pages ILEK même
// si le rôle a `ilek-dashboard` / `ilek-contracts` activé ici (cf. Layout.jsx).
export const DEFAULT_PERMISSIONS = {
  admin:   ['dashboard','quality-overview','contracts','suivi-client','mes-objectifs','agence-map','secteurs','outils','infos','formation','chat','voitures','suivi-cartons','admin','ilek-dashboard','ilek-contracts','ilek-qualite','bouygues-dashboard','bouygues-contracts','assistant-ia','organigramme'],
  partner: ['dashboard','quality-overview','contracts','suivi-client','outils','infos','chat','suivi-cartons','ilek-dashboard','ilek-contracts','ilek-qualite','bouygues-dashboard','bouygues-contracts'],
  manager: ['dashboard','contracts','suivi-client','mes-objectifs','secteurs','outils','infos','suivi-cartons','seller-space','ilek-dashboard','ilek-contracts','ilek-qualite','bouygues-dashboard','bouygues-contracts'],
  vendor:  ['dashboard','contracts','suivi-client','mes-objectifs','outils','infos','seller-space','ilek-dashboard','ilek-contracts','bouygues-dashboard','bouygues-contracts'],
  // Point de départ = pages du manager, sans Suivi RH (résolu dynamiquement côté backend).
  responsable_agence: ['dashboard','contracts','suivi-client','mes-objectifs','secteurs','outils','infos','seller-space','ilek-dashboard','ilek-contracts','ilek-qualite','bouygues-dashboard','bouygues-contracts'],
};

export const ALL_PAGES = [
  { key: 'dashboard',            label: 'Dashboard',               group: 'Octopus Energy',  lockedOn: ['admin','partner','manager','vendor','responsable_agence'] },
  { key: 'quality-overview',     label: 'Overview Qualité',         group: 'Octopus Energy' },
  { key: 'contracts',            label: 'Contrats',                 group: 'Octopus Energy' },
  { key: 'suivi-client',         label: 'Suivi client',             group: 'Octopus Energy' },
  { key: 'secteurs',             label: 'Secteur et mairie',        group: 'Octopus Energy' },
  { key: 'infos',                label: 'Infos',                    group: 'RT Distribution' },
  { key: 'ilek-dashboard',       label: 'Dashboard',                group: 'ILEK' },
  { key: 'ilek-contracts',       label: 'Contrats',                 group: 'ILEK' },
  { key: 'ilek-qualite',         label: 'Qualité',                  group: 'ILEK' },
  { key: 'bouygues-dashboard',   label: 'Dashboard',                group: 'Bouygues' },
  { key: 'bouygues-contracts',   label: 'Contrats',                 group: 'Bouygues' },
  { key: 'outils',               label: 'Outils et Demandes',       group: 'RT Distribution' },
  { key: 'formation',            label: 'Formation',                group: 'RT Distribution' },
  { key: 'chat',                 label: 'Chat',                     group: 'RT Distribution' },
  { key: 'voitures',             label: 'Voitures',                 group: 'RT Distribution' },
  { key: 'suivi-cartons',        label: 'Suivi RH',                 group: 'RT Distribution', lockedOff: ['responsable_agence'] },
  { key: 'mes-objectifs',        label: 'Mes Objectifs',            group: 'RT Distribution', lockedOff: ['partner'] },
  { key: 'agence-map',           label: 'Météo des Agences',        group: 'RT Distribution', lockedOff: ['partner','manager','vendor'] },
  { key: 'seller-space',         label: 'Mon espace (vendeur)',     group: 'RT Distribution', lockedOff: ['admin','partner'] },
  { key: 'assistant-ia',         label: 'Assistant IA',             group: 'RT Distribution' },
  { key: 'organigramme',         label: 'Organigramme',             group: 'RT Distribution', lockedOff: ['partner','manager','vendor','responsable_agence'] },
  { key: 'admin',                label: 'Administration',           group: 'RT Distribution', lockedOn: ['admin'], lockedOff: ['partner','manager','vendor','responsable_agence'] },
];

const PermissionsContext = createContext({
  canAccess: () => true,
  myPages: [],
  allPermissions: null,
  savePermissions: async () => {},
  loading: false,
  reloadMyPermissions: async () => {},
});

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [myPages, setMyPages] = useState(null);        // current user's pages
  const [allPermissions, setAllPermissions] = useState(null); // master admin only
  const [loading, setLoading] = useState(false);
  const MASTER = 'bo.rtdistribution@gmail.com';

  // Relies on axios.defaults.headers.common['Authorization'] set by AuthContext
  const reloadMyPermissions = useCallback(async () => {
    if (!user) { setMyPages(null); return; }
    const canonicalRole = normalizeRole(user.role);
    try {
      const res = await axios.get(`${API_URL}/api/permissions/me`);
      setMyPages(res.data.allowed_pages || DEFAULT_PERMISSIONS[canonicalRole] || []);
    } catch {
      setMyPages(DEFAULT_PERMISSIONS[canonicalRole] || []);
    }
  }, [user]);

  // Load current user's permissions on login
  useEffect(() => { reloadMyPermissions(); }, [reloadMyPermissions]);

  // Master admin: also load full permissions matrix
  useEffect(() => {
    if (user?.email?.toLowerCase() !== MASTER) return;
    setLoading(true);
    axios.get(`${API_URL}/api/permissions`)
      .then(res => setAllPermissions(res.data))
      .catch(() => setAllPermissions({ ...DEFAULT_PERMISSIONS }))
      .finally(() => setLoading(false));
  }, [user]);

  const canAccess = useCallback((pageKey) => {
    if (!user) return false;
    const canonicalRole = normalizeRole(user.role);
    const pages = myPages ?? DEFAULT_PERMISSIONS[canonicalRole] ?? [];
    return pages.includes(pageKey);
  }, [user, myPages]);

  const savePermissions = async (newPerms) => {
    await axios.put(`${API_URL}/api/permissions`, newPerms);
    setAllPermissions(newPerms);
    // Reload current user's permissions so nav updates immediately
    await reloadMyPermissions();
  };

  return (
    <PermissionsContext.Provider value={{ canAccess, myPages, allPermissions, savePermissions, loading, reloadMyPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export const usePermissions = () => useContext(PermissionsContext);
